<script setup lang="ts">
import { ref, watch, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { fetchAccountSingle as fetchAccountSingleAdmin } from '@/api/accountsAdmin'
import { fetchAccountSingle as fetchAccountSingleClient } from '@/api/accountsClient'
import { useUserStore } from '@/stores/userStore'

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()

const friendlyName = ref<string>('')
const logoUrl = ref<string>('')

const isAdmin = ['company_user', 'company_admin', 'company_suadmin', 'company_owner'].includes(
  String(userStore?.role || '')
)

// Keep tabs in sync with URL hash
const productRouteNames = new Set([
  'AllProductsAccounts',
  'AddProduct',
  'EditAccountProducts',
  'ViewAccountProduct',
  'ViewAccountProductsJust',
])

const isProductsContext = () => {
  if (route.hash === '#products') return true
  if (productRouteNames.has(String(route.name))) return true
  // Fallback: path contains '/products/' under account
  return (
    /\/account(-admin)?\/.*\/products/.test(route.path) || /\/account\/products\//.test(route.path)
  )
}

const activeTab = computed({
  get: () => (isProductsContext() ? 'products' : 'info'),
  set: (val: 'info' | 'products') => {
    const nameParam = String(
      (route.params as any).name ||
        (route.params as any).account ||
        (!isAdmin ? (userStore as any)?.juid : '')
    )
    // Always navigate to the account detail pages for tabs
    if (val === 'products') {
      router.push(
        isAdmin ? `/account-admin/${nameParam}#products` : `/account/${nameParam}#products`
      )
    } else {
      router.push(isAdmin ? `/account-admin/${nameParam}#info` : `/account/${nameParam}#info`)
    }
  },
})

// Hide tabs on Users page, Create User page, and on client /account/ route
const showTabs = computed<boolean>(() => {
  const name = String(route.name || '')
  const onClientAccountRoute = route.path.startsWith('/account/')
  const onCreateUserPage = route.path === '/users/create-user'
  return (
    name !== 'UsersViewClient' &&
    route.path !== '/users' &&
    !onClientAccountRoute &&
    !onCreateUserPage
  )
})

const getFolder = (): string => {
  const fromParams = String((route.params as any).name || (route.params as any).account || '')
  if (fromParams) return fromParams
  // Fallback for client context (e.g., /users route)
  if (!isAdmin && (userStore as any)?.juid) return String((userStore as any).juid)
  return ''
}

const loadHeader = async () => {
  const folder = getFolder()
  if (!folder) return

  try {
    const data = isAdmin
      ? await fetchAccountSingleAdmin(folder)
      : await fetchAccountSingleClient(folder)
    friendlyName.value = data?.accountNameFriendly || data?.accountName || folder
    logoUrl.value = data?.image || ''
  } catch (e) {
    friendlyName.value = folder
  }
}

const navigateToInfo = () => {
  const folder = getFolder()
  if (!folder) return
  const path = isAdmin ? `/account-admin/${folder}#info` : `/account/${folder}#info`
  router.push(path)
}

watch(
  () => [(route.params as any).name, (route.params as any).account, (userStore as any)?.juid],
  () => loadHeader()
)
onMounted(loadHeader)
onMounted(() => {
  // Do not add a hash on pages without tabs (e.g., /users)
  if (!showTabs.value) return
  if (!route.hash) {
    // Default to PRODUCTS on the Admin Account Details route (/account-admin/:name).
    // Keep existing behavior elsewhere to avoid surprising navigation on non-account pages.
    const onAdminAccountDetailsRoute =
      route.path.startsWith('/account-admin/') &&
      !route.path.startsWith('/account-admin/edit/') &&
      !!String((route.params as any)?.name || '')

    const defaultHash = onAdminAccountDetailsRoute ? '#products' : isProductsContext() ? '#products' : '#info'
    router.replace({ hash: defaultHash })
  }
})
</script>

<template>
  <v-toolbar
    color="primary"
    density="comfortable"
    flat
    class="account-header pa-2"
    style="min-height: 64px"
  >
    <template #default>
      <div class="d-flex align-center justify-space-between w-100 toolbar-content">
        <div
          class="d-flex align-center pl-2 account-title"
          style="gap: 12px"
          @click="navigateToInfo"
        >
          <v-avatar v-if="logoUrl" size="48" class="bg-white pa-1">
            <img :src="logoUrl" alt="Account Logo" class="w-100" />
          </v-avatar>
          <div class="text-h5 font-weight-medium">{{ friendlyName }}</div>
        </div>
        <v-tabs
          v-if="showTabs"
          v-model="activeTab"
          bg-color="transparent"
          class="text-white tabs-container"
          density="comfortable"
          center-active
        >
          <v-tab data-testid="info-tab" value="info">INFO</v-tab>
          <v-tab data-testid="products-tab" value="products">PRODUCTS</v-tab>
        </v-tabs>
      </div>
    </template>
  </v-toolbar>
</template>

<style scoped>
.account-header {
  position: sticky;
  top: 0;
  z-index: 5;
}

.account-header :deep(.v-toolbar__content) {
  height: auto !important;
}

.account-title {
  cursor: pointer;
}

/* Desktop: tabs centered */
@media (min-width: 768px) {
  .tabs-container {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  }
}

/* Mobile: stack vertically using flexbox */
@media (max-width: 767px) {
  .account-header :deep(.v-toolbar__content) {
    padding-top: 8px !important;
    padding-bottom: 8px !important;
  }

  .toolbar-content {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 12px;
  }

  .account-title {
    width: 100%;
  }

  .tabs-container {
    width: 100%;
    border-top: 1px solid rgba(255, 255, 255, 0.2);
    padding-top: 8px;
  }

  .account-header :deep(.v-slide-group) {
    width: 100%;
  }

  .account-header :deep(.v-slide-group__content) {
    justify-content: center;
  }
}

/* Tab styling */
.account-header :deep(.v-tabs .v-tab) {
  letter-spacing: 0.08em;
  font-weight: 600;
  padding: 8px 20px;
  opacity: 0.75;
  border-bottom: 3px solid transparent;
  transition: all 0.2s ease;
}

.account-header :deep(.v-tabs .v-tab:hover) {
  opacity: 1;
}

.account-header :deep(.v-tabs .v-slide-group__content) {
  gap: 16px;
}

.account-header :deep(.v-tab.v-tab--selected) {
  color: #ffffff !important;
  font-weight: 700;
  opacity: 1;
  border-bottom: 3px solid #ffffff;
}

/* Remove default slider indicators */
.account-header :deep(.v-tabs__slider),
.account-header :deep(.v-tab__slider),
.account-header :deep(.v-tabs .v-tabs__slider),
.account-header :deep(.v-tabs .v-tab__slider),
.account-header :deep(.v-tabs .v-tabs-slider),
.account-header :deep(.v-tab .v-tab__slider) {
  display: none !important;
}

.account-header :deep(.v-tabs),
.account-header :deep(.v-slide-group__container) {
  border-bottom: 0 !important;
}
</style>
